import type { IGridColumn, ISnapshotUsage } from '../api/contracts';
import { gridRows } from './snapshot-view';

/**
 * Which cells of a grid or multi-year table the respondent writes — **a mirror of survey-api's
 * `TableLayout`** (`Domain/Engine/TableLayout.cs`, SR-416's table-arithmetic design §4), which
 * expands a table's `computed`, `na_cells` and `heading_rows` into read-only cells. It moves with
 * `TableLayout.Build` / `Expand`.
 *
 * A mirror rather than a reading because nothing on the wire flags a cell: the expansion is the
 * only way to know which keys a row write may carry, and survey-api's `AnswerWriteService` refuses
 * a row naming a computed or N/A column — whatever its value — or any cell of a heading row,
 * `cell_read_only`, **with every other answer in the batch**. Catching the refusal would still
 * lose the batch (the roadmap's D3), so the write is built from this.
 *
 * It decides which cells may be written and how a cell is drawn, never a value: a computed cell's
 * value is the engine's, in the row's own stored cells. Read from `params` alone, and total: a
 * table with none of the three members (July content, an API before SR-416) is every cell
 * `input`, as it always was.
 *
 * The expansion, as `TableLayout` makes it: N/A cells only on rows that hold cells; a column form
 * (`{column, expr}`) in every row that is no heading; a row sum (`{row, sum_rows, columns?}`) on
 * its row — every summed row a cell row, else nothing — over its named numeric columns, else every
 * numeric one; a cell form (`{row, column, expr}`) on a cell row. Only a numeric column is ever a
 * computed target (`GridColumn.IsNumeric`), and **N/A wins** over every form. What `TableLayout`
 * reports as a publish problem (an unknown row or column, a cycle) never reaches a published
 * definition, so it is not re-checked here.
 *
 * `e2e/table-layout.ts` is the suite's own reading of the same expansion, kept apart on purpose:
 * a spec that asked the app which cells are writable would test the app against itself.
 */

/** What a cell is: a heading row's, not applicable, worked out by the engine, or typed. */
export type CellKind = 'heading' | 'na' | 'computed' | 'input';

export interface ITableLayout {
  /** The cell's kind. A row or column the table does not declare is `input`, as survey-api's
   *  write accepts a column it does not declare. */
  kind(row: string, column: string): CellKind;
  /** Whether the row is a heading row, holding no cell. */
  isHeading(row: string): boolean;
  /** The columns of `row` the respondent writes, in declared order — none on a heading row. A
   *  row write carries these and only these. */
  writableColumns(row: string): IGridColumn[];
}

/** survey-api's `GridColumn.IsNumeric`: the inputs whose cell holds a number. */
const NUMERIC_INPUTS: ReadonlySet<IGridColumn['input']> = new Set([
  'int',
  'num',
  'currency',
  'percent'
]);

/** A table's layout, from its usage's params; every cell `input` for any other usage. */
export function tableLayout(usage: ISnapshotUsage): ITableLayout {
  const grid = usage.params?.grid;
  const years = usage.params?.multi_year;
  const columns = grid?.columns ?? years?.columns ?? [];
  const rows = grid
    ? gridRows(usage).map((row) => row.key)
    : (years?.offsets ?? []).map((offset) => `y${offset}`);
  const declared = new Set(rows);
  const headings = new Set((grid?.heading_rows ?? []).filter((row) => declared.has(row)));
  const isCellRow = (row: string) => declared.has(row) && !headings.has(row);
  const numeric = new Set(
    columns.filter((column) => NUMERIC_INPUTS.has(column.input)).map((column) => column.code)
  );

  const na = new Set<string>();
  const computed = new Set<string>();
  const cell = (row: string, column: string) => `${row}|${column}`;

  for (const [row, naColumns] of Object.entries(grid?.na_cells ?? years?.na_cells ?? {})) {
    if (!isCellRow(row)) continue;
    for (const column of naColumns) na.add(cell(row, column));
  }
  const compute = (row: string, column: string) => {
    if (numeric.has(column) && !na.has(cell(row, column))) computed.add(cell(row, column));
  };
  for (const entry of grid?.computed ?? years?.computed ?? []) {
    if ('sum_rows' in entry) {
      if (!isCellRow(entry.row) || !entry.sum_rows.every(isCellRow)) continue;
      for (const column of entry.columns ?? [...numeric]) compute(entry.row, column);
    } else if ('row' in entry) {
      if (isCellRow(entry.row)) compute(entry.row, entry.column);
    } else {
      for (const row of rows) if (!headings.has(row)) compute(row, entry.column);
    }
  }

  const kind = (row: string, column: string): CellKind => {
    if (headings.has(row)) return 'heading';
    if (na.has(cell(row, column))) return 'na';
    if (computed.has(cell(row, column))) return 'computed';
    return 'input';
  };
  return {
    kind,
    isHeading: (row) => headings.has(row),
    writableColumns: (row) => columns.filter((column) => kind(row, column.code) === 'input')
  };
}

/**
 * The columns a write of one row carries: the row's writable columns for a table, none for any
 * other usage — the `columns` argument of `encodeAnswer` and `isMalformed` at every caller that
 * writes (B4-2). A computed or N/A cell is then neither sent nor able to hold a write back.
 */
export function rowWriteColumns(usage: ISnapshotUsage, row: string): IGridColumn[] {
  return usage.params?.grid || usage.params?.multi_year
    ? tableLayout(usage).writableColumns(row)
    : [];
}
